import { useLang } from "@/context/LanguageContext";
import {
  DEVELOPER_HEADINGS, DEVELOPER_LECTURES, DEVELOPER_BOOK, DEVELOPER_COLUMNS,
  DEVELOPER_SCIENTIFIC, DEVELOPER_ADDITIONAL, DEVELOPER_TEACHING, DEVELOPER_AWARDS,
} from "@/i18n/developer_data";
import { Mic, BookOpen, PenLine, FlaskConical, Layers, GraduationCap, Award, ExternalLink } from "lucide-react";

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section className="card-mift p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-blue-300">
        <Icon className="w-4 h-4" />
        <h3 className="text-sm font-semibold uppercase tracking-widest">{title}</h3>
      </div>
      <div className="mt-4 text-sm text-slate-300 leading-relaxed">{children}</div>
    </section>
  );
}

export default function Developer() {
  const { t, lang } = useLang();
  const H = DEVELOPER_HEADINGS[lang] || DEVELOPER_HEADINGS.en;
  const items = [t.developer.c1, t.developer.c2, t.developer.c3, t.developer.c4,
                 t.developer.c5, t.developer.c6, t.developer.c7, t.developer.c8];

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20" data-testid="developer-page">
      <h1 className="text-4xl sm:text-5xl font-bold">{t.developer.title}</h1>

      <div className="mt-12 grid lg:grid-cols-3 gap-8">
        <div className="card-mift p-6 min-h-[420px] flex flex-col items-center justify-center" data-testid="developer-photo">
          <img src="/assets/developer_photo.jpg" alt="Isabella Calamia Rinaldi"
               className="w-48 h-48 rounded-full object-cover ring-4 ring-blue-500/20 shadow-[0_20px_60px_-15px_rgba(37,99,235,0.35)]"
               data-testid="developer-photo-image" />
          <p className="mt-6 text-sm text-slate-200 font-medium">{t.developer.name}</p>
          <p className="mt-1 text-xs text-slate-500 text-center max-w-[16rem]">
            Brazilian attorney · Developer of the MIFT methodology
          </p>
        </div>

        <div className="lg:col-span-2">
          <h2 className="text-2xl font-semibold text-blue-300">{t.developer.name}</h2>
          <p className="mt-3 text-slate-300 leading-relaxed">{t.developer.role}</p>

          <h3 className="mt-10 text-lg font-semibold text-slate-100">{t.developer.credentials_title}</h3>
          <ul className="mt-4 space-y-3 text-slate-300 text-sm">
            {items.map((c, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Verified professional record */}
      <div className="mt-16 grid gap-6 lg:grid-cols-2">
        <Section icon={Mic} title={H.lectures} testid="dev-lectures">
          <ul className="space-y-4">
            {DEVELOPER_LECTURES.map((l, i) => (
              <li key={i} className="border-l-2 border-blue-500/40 pl-4">
                <div className="text-slate-100 font-medium">"{l.title}"</div>
                <div className="text-xs text-slate-500 mt-1">{l.role} · {l.venue} · {l.year}</div>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={Award} title={H.awards} testid="dev-awards">
          <ul className="space-y-3">
            {DEVELOPER_AWARDS.map((a, i) => (
              <li key={i} className="border-l-2 border-amber-500/40 pl-4">
                <div className="text-slate-100 font-medium">{a.title}</div>
                <div className="text-xs text-slate-500 mt-1">{a.year} · {a.venue}</div>
              </li>
            ))}
          </ul>
        </Section>

        <div className="lg:col-span-2 grid gap-6">
          <Section icon={BookOpen} title={`${H.publications} — ${H.book}`} testid="dev-book">
            <p>{DEVELOPER_BOOK.citation}</p>
            <p className="mt-3 text-xs text-slate-400 italic">{H.book_ranking_note}</p>
          </Section>

          <Section icon={PenLine} title={H.columnist} testid="dev-columnist">
            <p className="text-slate-400 text-xs">{H.columnist_desc}</p>
            <ul className="mt-4 space-y-3">
              {DEVELOPER_COLUMNS.map((c, i) => (
                <li key={i} className="border-l-2 border-blue-500/30 pl-4">
                  <div className="text-slate-200">"{c.title}"</div>
                  <div className="text-xs text-slate-500 mt-1">{c.date}</div>
                </li>
              ))}
            </ul>
          </Section>

          <Section icon={FlaskConical} title={H.scientific_article} testid="dev-scientific">
            <p>{DEVELOPER_SCIENTIFIC.citation}</p>
            <a href={DEVELOPER_SCIENTIFIC.doi_url} target="_blank" rel="noreferrer noopener"
               className="mt-3 inline-flex items-center gap-1.5 text-blue-300 hover:text-blue-200 text-sm">
              DOI <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </Section>

          <Section icon={Layers} title={H.additional} testid="dev-additional">
            <ul className="space-y-3">
              {DEVELOPER_ADDITIONAL.map((p, i) => (
                <li key={i} className="border-l-2 border-slate-700 pl-4 text-slate-300">{p}</li>
              ))}
            </ul>
          </Section>

          <Section icon={GraduationCap} title={H.teaching} testid="dev-teaching">
            <div className="text-xs text-slate-400 mb-4">{H.teaching_block}</div>
            {DEVELOPER_TEACHING.map((tt, i) => (
              <div key={i} className="border-l-2 border-blue-500/40 pl-4">
                <div className="text-slate-100 font-medium">{tt.role} — "{tt.title}"</div>
                <div className="text-xs text-slate-500 mt-1">{tt.venue} · {tt.period}</div>
                <p className="mt-2 text-sm text-slate-300">{tt.desc}</p>
              </div>
            ))}
          </Section>
        </div>
      </div>
    </div>
  );
}
